/**
 * SupportLayoutController — Stimulus entry point for the /admin/support inbox.
 *
 * Owns all application state (conversations, filters, loading).
 * Fetches data from the JSON API and re-renders the Preact SupportApp
 * after each state change. This is the same "re-render on state change"
 * pattern used throughout this app.
 *
 * Subscribes to SupportInboxChannel (Action Cable) to receive real-time
 * new_email notifications and auto-refresh the conversation list.
 *
 * Persists the selected mailbox ID in localStorage under STORAGE_KEY so the
 * user's last choice is restored on the next visit.
 */

import { Controller } from '@hotwired/stimulus'
import { render, h } from 'preact'
import { SupportApp }  from '../components/support/SupportApp.jsx'
import { subscribe }   from '../lib/cable'

const STORAGE_KEY = 'support_inbox_mailbox_id'

export default class extends Controller {
  static values = {
    mailboxes:        { type: Array,  default: [] },
    currentMailboxId: { type: Number, default: 0 },
  }

  // ── State ──────────────────────────────────────────────────────────────────

  _state = {
    conversations: [],
    statusCounts:  {},
    loading:       true,
    statusFilter:  null,
    searchQuery:   '',
    currentPage:   1,
    hasMore:       false,
    mailboxId:     null,
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  connect() {
    this._state.mailboxId = this._loadMailboxId()
    this._render()
    this._fetch(1)
    this._subscribeCable()
  }

  disconnect() {
    this._cableSub?.unsubscribe()
    this._cableSub = null
    render(null, this.element)
  }

  // ── Private ────────────────────────────────────────────────────────────────

  _loadMailboxId() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const id = Number(stored)
        // Validate that the stored ID still exists in the available mailboxes
        const valid = (this.mailboxesValue || []).some(mb => mb.id === id)
        if (valid) return id
      }
    } catch (_) { /* localStorage unavailable */ }
    return null
  }

  _saveMailboxId(id) {
    try {
      if (id) {
        localStorage.setItem(STORAGE_KEY, String(id))
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
    } catch (_) { /* localStorage unavailable */ }
  }

  _subscribeCable() {
    this._cableSub = subscribe(
      { channel: 'SupportInboxChannel' },
      {
        received: (data) => {
          if (data.type === 'new_email') {
            // Only refresh if we're viewing the affected mailbox (or all mailboxes)
            const watchingAll     = !this._state.mailboxId
            const watchingMailbox = this._state.mailboxId === data.mailbox_id
            if (watchingAll || watchingMailbox) {
              this._fetch(1)
            }
          }
        },
      }
    )
  }

  async _fetch(page = 1) {
    this._setState({ loading: true })

    const params = new URLSearchParams({ page })
    if (this._state.statusFilter) params.set('status', this._state.statusFilter)
    if (this._state.searchQuery)  params.set('q', this._state.searchQuery)
    if (this._state.mailboxId)    params.set('mailbox_id', this._state.mailboxId)

    try {
      const res = await fetch(`/admin/support.json?${params}`, {
        headers: { Accept: 'application/json' }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const emails = data.emails || []

      this._setState({
        conversations: page === 1 ? emails : [...this._state.conversations, ...emails],
        statusCounts:  data.status_counts || {},
        loading:       false,
        currentPage:   page,
        hasMore:       data.meta ? page < data.meta.last_page : false,
      })
    } catch (err) {
      console.error('[SupportLayoutController] fetch error', err)
      this._setState({ loading: false })
    }
  }

  _setState(updates) {
    Object.assign(this._state, updates)
    this._render()
  }

  _render() {
    render(
      h(SupportApp, {
        conversations:     this._state.conversations,
        statusCounts:      this._state.statusCounts,
        loading:           this._state.loading,
        searchQuery:       this._state.searchQuery,
        mailboxes:         this.mailboxesValue || [],
        currentMailboxId:  this._state.mailboxId,
        onFetch:           () => this._fetch(1),
        onFilter:          status => {
          this._setState({ statusFilter: status })
          this._fetch(1)
        },
        onSearch:          query => {
          this._setState({ searchQuery: query })
          this._fetch(1)
        },
        onMailboxChange:   mailboxId => {
          this._saveMailboxId(mailboxId)
          this._setState({ mailboxId })
          this._fetch(1)
        },
        onLoadMore:        () => {
          if (this._state.hasMore && !this._state.loading) {
            this._fetch(this._state.currentPage + 1)
          }
        },
      }),
      this.element
    )
  }
}
